import { ImageLibraryBrowseShell } from '@/components/image-library/image-library-browse-shell';
import { EMPTY_IMAGE_LIBRARY_SUBJECTS, useImageLibraryListing } from '@/components/image-library/use-image-library-listing';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { MaImage } from '@/lib/zybooks-api';
import { useCallback, useMemo, useState } from 'react';

export interface ImageLibraryPickerModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onImageSelected: (image: MaImage) => void;
    defaultOnlyUnused?: boolean;
    defaultSelectedSubjects?: string[];
}

export function ImageLibraryPickerModal({
    open,
    onOpenChange,
    onImageSelected,
    defaultOnlyUnused = true,
    defaultSelectedSubjects = EMPTY_IMAGE_LIBRARY_SUBJECTS,
}: ImageLibraryPickerModalProps) {
    const [selected, setSelected] = useState<MaImage | null>(null);

    const listing = useImageLibraryListing({
        enabled: open,
        defaultOnlyUnused,
        defaultSelectedSubjects,
    });

    const { allImages, resetAllSearchAndFilters } = listing;

    const handleDialogOpenChange = useCallback(
        (next: boolean) => {
            if (!next) {
                setSelected(null);
                resetAllSearchAndFilters();
            }
            onOpenChange(next);
        },
        [onOpenChange, resetAllSearchAndFilters],
    );

    const selectedImage = useMemo(() => {
        if (selected === null) {
            return null;
        }
        return allImages.find(img => img.ma_image_id === selected.ma_image_id) ?? null;
    }, [allImages, selected]);

    const handlePickerSelect = useCallback(
        (image: MaImage) => {
            if (selected?.ma_image_id === image.ma_image_id) {
                setSelected(null);
                return;
            }
            setSelected(image);
        },
        [selected],
    );

    const handleCancel = useCallback(() => {
        handleDialogOpenChange(false);
    }, [handleDialogOpenChange]);

    const handleInsert = useCallback(() => {
        if (!selectedImage) return;
        onImageSelected(selectedImage);
        handleDialogOpenChange(false);
    }, [handleDialogOpenChange, onImageSelected, selectedImage]);

    return (
        <Dialog
            open={open}
            onOpenChange={handleDialogOpenChange}
        >
            <DialogContent className="flex flex-col h-[95vh] sm:max-w-[95vw]">
                <DialogHeader>
                    <DialogTitle>Insert image from library</DialogTitle>
                </DialogHeader>
                <DialogDescription>Select an image from the library to insert into the document.</DialogDescription>

                <ImageLibraryBrowseShell
                    listing={listing}
                    imageGridProps={{
                        onPick: handlePickerSelect,
                        pickerSelected: selected,
                    }}
                />

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        disabled={!selectedImage}
                        onClick={handleInsert}
                    >
                        Insert
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
